import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { ArrowLeft, MoreHorizontal, X } from 'lucide-react';
import { useIsXLDesktop, useIsDesktop } from '@/hooks/use-device-context';
import { useDetailPanel } from './detail-panel-context';
import { PanelModeContext } from './panel-mode-context';

const TOK = {
  bg: '#0e151c',
  panel: '#18222e',
  panelHi: '#1f2a38',
  border: 'rgba(255,255,255,0.08)',
  borderHi: 'rgba(255,255,255,0.14)',
  text: '#fff',
  text2: 'rgba(255,255,255,0.72)',
  hint: 'rgba(255,255,255,0.5)',
  accent: '#c98ad9',
  blue: '#2565eb',
} as const;

interface DetailPanelProps {
  /** Breadcrumb shown in the header chrome (e.g. PROFILE, EVENTO). */
  crumb?: string;
  /** Slot 2 — hero (cover/avatar/poster, 16:9 max). */
  hero?: ReactNode;
  /** Slot 3 — title + meta block. */
  title?: ReactNode;
  /** Slot 4 — action row (3 buttons grid 2fr/1fr/1fr). */
  actions?: ReactNode;
  /** Slot 5 — body (where the existing components are pasted). */
  children?: ReactNode;
  /** Slot 6 — sticky footer (only used by post composer). */
  footer?: ReactNode;
  /** Show the dashed slot labels (placeholder for unfilled slots). */
  showSlotPlaceholders?: boolean;
  /** When true, render the children as a raw single body filling the
   *  panel (bypass the 5-slot layout with padding). Used to embed an
   *  existing sheet via panelMode without layering padding/slots. */
  bareBody?: boolean;
  /** Extra control rendered inside the chrome bar, before More/Close
   *  (e.g. the "Modifica" toggle for own profile). */
  chromeAction?: ReactNode;
  /** Panel width profile.
   *  - 'narrow' (default): identity/peek content (profile, post, chat,
   *    notifications) — anchored left of the chat rail at ~360-440px.
   *  - 'wide': rich, task-oriented content (event, group) — flush to the
   *    right edge (covers the chat rail) at a much larger, responsive width. */
  variant?: 'narrow' | 'wide';
}

function CHAT_RAIL_WIDTH(isXL: boolean, isDesktop: boolean) {
  if (isXL) return 380;
  if (isDesktop) return 300;
  return 260;
}
function PANEL_WIDTH(isXL: boolean, isDesktop: boolean) {
  if (isXL) return 440;
  if (isDesktop) return 400;
  return 360;
}

function SlotPlaceholder({
  tag,
  height,
  hint,
  ratio,
}: {
  tag: string;
  height?: number;
  hint?: string;
  ratio?: string;
}) {
  return (
    <div
      style={{
        border: `1px dashed ${TOK.borderHi}`,
        borderRadius: 12,
        padding: 14,
        minHeight: height ?? 72,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: 'rgba(255,255,255,0.02)',
        aspectRatio: ratio,
        alignItems: ratio ? 'center' : undefined,
        justifyContent: ratio ? 'center' : undefined,
        textAlign: ratio ? 'center' : undefined,
      }}
    >
      <span
        style={{
          fontSize: 9,
          letterSpacing: '0.18em',
          fontWeight: 700,
          color: TOK.accent,
        }}
      >
        SLOT · {tag}
      </span>
      {hint && (
        <span style={{ fontSize: 11, color: TOK.text2, lineHeight: 1.5 }}>{hint}</span>
      )}
    </div>
  );
}

export function DetailPanel({
  crumb,
  hero,
  title,
  actions,
  children,
  footer,
  showSlotPlaceholders = true,
  bareBody = false,
  chromeAction,
  variant = 'narrow',
}: DetailPanelProps) {
  const { stack, back, close } = useDetailPanel();
  const [location] = useLocation();
  const isDesktop = useIsDesktop();
  const isXL = useIsXLDesktop();
  // Portal target for in-panel sheets (see PanelModeContext).
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  // Back handler for an active in-panel sub-step overlay (participants, etc.).
  const [overlayBack, setOverlayBackState] = useState<(() => void) | null>(null);
  const setOverlayBack = useCallback(
    (handler: (() => void) | null) => setOverlayBackState(() => handler),
    []
  );

  // Mirror chat rail visibility from desktop-shell: from lg up, hidden on /chat.
  const railShown = isDesktop && !location.startsWith('/chat');
  const railOffset = railShown ? CHAT_RAIL_WIDTH(isXL, isDesktop) : 0;
  // Sidebar widths from DesktopSidebar: 240 full, 72 collapsed.
  const sidebarOffset = isDesktop ? 240 : 72;
  const canGoBack = stack.length > 1;

  // Width profile. The panel ALWAYS sits to the left of the conversations
  // (chat) rail — it never covers it. 'wide' content (event/group) just fills
  // more of the space between the sidebar and the rail; 'narrow' content
  // (profile/post/chat/notifications) keeps the slim fixed width.
  const isWide = variant === 'wide';
  const panelRight = railOffset;
  const panelWidth: string | number = isWide
    // Cap at 672px = Tailwind max-w-2xl, the event content column width
    // (single column, no two-column split).
    ? `clamp(360px, calc(100vw - ${sidebarOffset}px - ${railOffset}px - 48px), 672px)`
    : PANEL_WIDTH(isXL, isDesktop);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return createPortal(
    <>
      <div
        onClick={close}
        aria-hidden
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: sidebarOffset,
          right: panelRight,
          // Plain scrim — NO backdrop-filter blur: blur forces an expensive
          // re-rasterization on every frame while the panel content moves
          // (scroll/transitions), which is the main cause of the jank.
          background: 'rgba(8,12,18,0.62)',
          zIndex: 40,
        }}
      />
      <aside
        aria-label="Detail panel"
        data-detail-panel
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          right: panelRight,
          width: panelWidth,
          flex: 'none',
          maxWidth: 'none',
          minWidth: isWide ? undefined : panelWidth,
          background: 'var(--tg-bg-color)',
          borderLeft: `1px solid ${TOK.border}`,
          borderRight: `1px solid ${TOK.borderHi}`,
          // Lighter shadow (the old -40px/80px blur was very expensive to
          // repaint), and put the panel on its own compositor layer with
          // paint containment so content movement doesn't repaint outside it.
          boxShadow: '-12px 0 28px -12px rgba(0,0,0,0.55)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 41,
          color: TOK.text,
          transform: 'translateZ(0)',
          transition: 'none',
          contain: 'layout paint',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            height: 64,
            padding: '0 14px',
            borderBottom: `1px solid ${TOK.border}`,
            flexShrink: 0,
          }}
        >
          {overlayBack ? (
            // An in-panel sub-step (participants, payment, …) is open: show only
            // a back arrow that closes it and returns to the underlying view.
            <>
              <button type="button" onClick={overlayBack} aria-label="Indietro" style={chromeBtn}>
                <ArrowLeft size={16} strokeWidth={1.8} />
              </button>
              <span style={{ flex: 1 }} />
            </>
          ) : (
            <>
              {canGoBack ? (
                <button type="button" onClick={back} aria-label="Indietro" style={chromeBtn}>
                  <ArrowLeft size={16} strokeWidth={1.8} />
                </button>
              ) : (
                <span style={{ width: 30, height: 30 }} />
              )}
              <span
                style={{
                  flex: 1,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  color: TOK.hint,
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {crumb || ''}
              </span>
              {chromeAction}
              <button type="button" aria-label="Altro" style={chromeBtn}>
                <MoreHorizontal size={18} strokeWidth={1.8} />
              </button>
              <button type="button" onClick={close} aria-label="Chiudi" style={chromeBtn}>
                <X size={18} strokeWidth={1.8} />
              </button>
            </>
          )}
        </div>

        {bareBody ? (
          <div
            ref={setContainerEl}
            style={{ flex: 1, position: 'relative', overflow: 'hidden', contain: 'layout paint' }}
          >
            <PanelModeContext.Provider value={{ inPanel: true, container: containerEl, setOverlayBack }}>
              {children}
            </PanelModeContext.Provider>
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            {hero
              ? hero
              : showSlotPlaceholders && (
                  <SlotPlaceholder tag="HERO" ratio="16/9" hint="cover / avatar / poster · grafica esistente" />
                )}

            {title
              ? title
              : showSlotPlaceholders && (
                  <SlotPlaceholder tag="TITLE + META" hint="nome + 2-3 chip categoria/stato" />
                )}

            {actions
              ? actions
              : showSlotPlaceholders && (
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                    {['PRIMARY', 'SECONDARY', 'SECONDARY'].map((t, i) => (
                      <div
                        key={i}
                        style={{
                          height: 38,
                          border: `1px ${i === 0 ? 'solid' : 'dashed'} ${TOK.borderHi}`,
                          borderRadius: 9,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          letterSpacing: '0.14em',
                          color: TOK.accent,
                          fontWeight: 700,
                          background:
                            i === 0 ? 'rgba(201,138,217,0.15)' : 'transparent',
                        }}
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                )}

            {children
              ? children
              : showSlotPlaceholders && (
                  <SlotPlaceholder
                    tag="BODY"
                    height={200}
                    hint="dove si incollano i componenti del codebase (info, foto, validations, kinky test, ecc.)"
                  />
                )}
          </div>
        )}

        {footer && (
          <div
            style={{
              borderTop: `1px solid ${TOK.border}`,
              padding: '10px 14px',
              flexShrink: 0,
              background: TOK.panel,
            }}
          >
            {footer}
          </div>
        )}
      </aside>
    </>,
    document.body
  );
}

const chromeBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  background: 'transparent',
  border: 'none',
  color: TOK.text2,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

export default DetailPanel;
